import React, { useCallback, useEffect, useState } from "react";
import { MdConfirmationNumber } from "react-icons/md";
import { FilaChamadoResumo, FilaOrganizacao } from "../../../../interfaces/FilaOrganizacao";
import { TICKET_STATE_LABELS, TicketState } from "../../../../interfaces/Ticket";
import { getChamadosDaFila, getFilasDaOrganizacao, mensagemDaFila } from "../../../../services/filaOrganizacaoApi";
import FilaChamadoModal from "./FilaChamadoModal";

const fmtDate = (iso?: string | null) => (iso ? new Date(iso).toLocaleDateString("pt-BR") : "—");

const selectCls =
  "rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800 focus:border-dv-blue-500 focus:outline-none focus:ring-2 focus:ring-dv-blue-500/20 dark:border-white/10 dark:bg-navy-900 dark:text-white";
const lblCls = "block text-sm font-semibold text-gray-700 dark:text-gray-300";

/** Aba "Fila da equipe": chamados das filas da organização, para quem tem `itsm.fila.atender`. */
const FilaEquipeTab: React.FC = () => {
  const [filas, setFilas] = useState<FilaOrganizacao[]>([]);
  const [filaId, setFilaId] = useState("");
  const [estado, setEstado] = useState("");
  const [itens, setItens] = useState<FilaChamadoResumo[]>([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [selecionado, setSelecionado] = useState<number | null>(null);

  useEffect(() => {
    getFilasDaOrganizacao().then(setFilas).catch(() => setFilas([]));
  }, []);

  const carregar = useCallback(() => {
    setLoading(true);
    setErro(null);
    getChamadosDaFila({
      filaId: filaId ? Number(filaId) : undefined,
      estado: estado ? (Number(estado) as TicketState) : undefined,
    })
      .then((p) => setItens(p.items ?? []))
      .catch((e) => setErro(mensagemDaFila(e, "Não foi possível carregar a fila. Tente de novo em instantes.")))
      .finally(() => setLoading(false));
  }, [filaId, estado]);

  useEffect(carregar, [carregar]);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label htmlFor="fila-filtro-fila" className={lblCls}>Fila</label>
          <select id="fila-filtro-fila" className={selectCls} value={filaId} onChange={(e) => setFilaId(e.target.value)}>
            <option value="">Todas</option>
            {filas.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
          </select>
        </div>
        <div>
          <label htmlFor="fila-filtro-estado" className={lblCls}>Estado</label>
          <select id="fila-filtro-estado" className={selectCls} value={estado} onChange={(e) => setEstado(e.target.value)}>
            <option value="">Todos</option>
            {Object.entries(TICKET_STATE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
      </div>

      {loading ? (
        <div role="status" className="py-12 text-center text-sm text-gray-700 dark:text-gray-300">Carregando…</div>
      ) : erro ? (
        <p role="alert" className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900/40 dark:bg-red-900/20 dark:text-red-300">{erro}</p>
      ) : itens.length === 0 ? (
        <p className="rounded-xl border border-dashed border-gray-200 py-10 text-center text-sm text-gray-700 dark:border-white/10 dark:text-gray-300">
          Nenhum chamado na fila. Quando alguém da organização abrir um chamado, ele aparece aqui.
        </p>
      ) : (
        <div className="flex flex-col gap-2">
          {itens.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setSelecionado(t.id)}
              className="flex items-center justify-between gap-3 rounded-xl border border-gray-100 bg-gray-50 px-4 py-3 text-left transition-colors hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-dv-blue-500/40 dark:border-white/5 dark:bg-navy-900 dark:hover:bg-navy-700"
            >
              <div className="flex min-w-0 items-center gap-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white text-dv-blue-600 dark:bg-navy-800 dark:text-dv-cyan-300">
                  <MdConfirmationNumber className="h-4 w-4" aria-hidden="true" />
                </span>
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-navy-700 dark:text-white" title={t.subject}>{t.subject}</p>
                  <p className="truncate text-xs text-gray-600 dark:text-gray-400">
                    {t.number} · {t.queueName ?? "Sem fila"} · {t.atendenteNome ?? "Sem atendente"} · Aberto em {fmtDate(t.openedAt)}
                  </p>
                </div>
              </div>
              <span className="shrink-0 rounded-full bg-dv-blue-100 px-2.5 py-1 text-[11px] font-bold text-dv-blue-700 dark:bg-dv-blue-900/40 dark:text-dv-cyan-300">
                {TICKET_STATE_LABELS[t.state] ?? t.stateName}
              </span>
            </button>
          ))}
        </div>
      )}

      {selecionado !== null && (
        <FilaChamadoModal ticketId={selecionado} filas={filas} onClose={() => setSelecionado(null)} onChanged={carregar} />
      )}
    </div>
  );
};

export default FilaEquipeTab;
